/*
 * Heron Road product page behaviour.
 *
 * Three small things, none of which needs a framework:
 *   the thumbnail rail swaps the stage image,
 *   the option selects resolve to a variant and update price and availability,
 *   the recommendations section fetches its own markup.
 *
 * The page is built so that all three are enhancements. With this file absent
 * the first image still shows, the first available variant is still in the
 * form, and the recommendations block is simply empty.
 */
(() => {
  'use strict';

  /* ---------------------------------------------------------------- gallery */

  function startGallery(root) {
    const stage = root.querySelector('[data-hr-stage]');
    const thumbs = [...root.querySelectorAll('[data-hr-thumb]')];
    if (!stage || thumbs.length < 2) return;

    const counter = root.querySelector('[data-hr-counter]');
    let index = 0;

    /* Decoding first stops the stage flashing empty, but an unbounded wait
       means the highlight moves and the picture does not, which reads as a
       dead click. The wait is capped so a slow image swaps anyway and the
       browser paints it progressively. */
    const show = async (next) => {
      const wrapped = (next + thumbs.length) % thumbs.length;
      const thumb = thumbs[wrapped];
      const full = thumb && thumb.dataset.hrFull;
      if (!full) return;

      index = wrapped;
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === wrapped));
      if (counter) counter.textContent = `${wrapped + 1} / ${thumbs.length}`;
      /* Keep the active thumbnail in view, since the rail scrolls once there
         are more thumbnails than fit beside the stage. */
      thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });

      if (stage.src === full) return;
      const image = new Image();
      image.src = full;
      await Promise.race([
        image.decode().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, 400)),
      ]);
      stage.src = full;
      stage.removeAttribute('srcset');
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
      thumb.addEventListener('mouseenter', () => show(i));
    });

    root.querySelector('[data-hr-prev]')?.addEventListener('click', () => show(index - 1));
    root.querySelector('[data-hr-next]')?.addEventListener('click', () => show(index + 1));

    /* Arrow keys work only while the gallery holds focus, so they never fight
       the page for the left and right keys. */
    const gallery = root.querySelector('.hr-pdp__gallery');
    gallery?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
    });
  }

  /* --------------------------------------------------------------- variants */

  function startVariants(root) {
    const data = root.parentElement.querySelector('[data-hr-variants]')
      || document.querySelector('[data-hr-variants]');
    const idField = root.querySelector('[data-hr-variant-id]');
    const priceEl = root.querySelector('[data-hr-price]');
    const submit = root.querySelector('[data-hr-submit]');
    if (!data || !idField || !submit) return;

    let variants = [];
    try { variants = JSON.parse(data.textContent); } catch { return; }
    if (!variants.length) return;

    /* Hidden inputs carry the options that had only one value, so the key is
       always the full option set and the lookup never depends on how many of
       them happened to be rendered as dropdowns. */
    const fields = [...root.querySelectorAll('[data-hr-option]')]
      .sort((a, b) => Number(a.dataset.hrOption) - Number(b.dataset.hrOption));
    if (!fields.length) return;

    const apply = () => {
      const chosen = fields.map((f) => f.value);
      const match = variants.find(
        (v) => v.options.length === chosen.length && v.options.every((o, i) => o === chosen[i])
      );

      if (!match) {
        submit.disabled = true;
        submit.textContent = 'Unavailable';
        return;
      }

      idField.value = match.id;
      submit.disabled = !match.available;
      submit.textContent = match.available ? 'Add to bag' : 'Sold out';

      if (priceEl) {
        priceEl.innerHTML = match.compare
          ? `<span class="hr-card__was">${match.compare}</span>${match.price}`
          : match.price;
      }

      /* replaceState rather than pushState: choosing a size should not add a
         history entry the back button has to walk through. */
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    };

    fields.forEach((f) => f.addEventListener('change', apply));
    apply();
  }

  /* --------------------------------------------------------------- quantity */

  function startQuantity(root) {
    root.querySelectorAll('[data-hr-qty]').forEach((box) => {
      const input = box.querySelector('input');
      if (!input) return;
      const nudge = (by) => {
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + by);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      box.querySelector('[data-hr-qty-down]')?.addEventListener('click', () => nudge(-1));
      box.querySelector('[data-hr-qty-up]')?.addEventListener('click', () => nudge(1));
    });
  }

  /* -------------------------------------------------------- recommendations */

  function startRecommendations() {
    document.querySelectorAll('[data-hr-recommendations]').forEach(async (holder) => {
      const url = holder.dataset.url;
      if (!url) return;
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const markup = await response.text();
        const fresh = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-hr-recommendations]');
        if (!fresh || !fresh.innerHTML.trim()) return;
        holder.innerHTML = fresh.innerHTML;
        /* These cards arrived after the reveal observer had already run, so
           they are shown outright rather than left stranded at zero opacity. */
        holder.querySelectorAll('.hr-reveal').forEach((el) => el.classList.add('is-visible'));
      } catch { /* a missing shelf is not worth an error on the page */ }
    });
  }

  /* ------------------------------------------------------------------- boot */

  const boot = () => {
    document.querySelectorAll('[data-hr-product]').forEach((root) => {
      startGallery(root);
      startVariants(root);
      startQuantity(root);
    });
    startRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
